import { ApolloCache, MutationUpdaterFunction } from '@apollo/client';

import {
  IdentityFragment,
  ReactionSummaryFragment,
  ReactionSummaryFragmentDoc,
  UndoReactionMutation,
  UndoReactionMutationVariables,
  getTypeOrNull,
  isError,
} from '@spotify/confidence-graphql';

import { reactionSummaryNameFromResource } from '../../utils';

/**
 * Creates an Apollo cache update function for when a reaction is undone.
 * Reads the ReactionSummary fragment and removes the current user's reaction.
 */
export function undoReactionCacheUpdate(
  currentIdentity: IdentityFragment | null | undefined,
): MutationUpdaterFunction<
  UndoReactionMutation,
  UndoReactionMutationVariables,
  {},
  ApolloCache<any>
> {
  return (cache, { data }, updateOptions) => {
    // Only update cache if the mutation succeeded
    if (!data || isError(data.undoReaction)) {
      return;
    }

    if (!currentIdentity) {
      return;
    }

    const variables = updateOptions.variables;
    if (!variables) {
      return;
    }

    const { resource, emoji } = variables;

    const summaryName = reactionSummaryNameFromResource(resource);

    const cacheId = cache.identify({
      __typename: 'CollaborationV1ReactionSummary',
      name: summaryName,
    });

    if (!cacheId) {
      return;
    }

    cache.updateFragment<ReactionSummaryFragment>(
      {
        id: cacheId,
        fragment: ReactionSummaryFragmentDoc,
        fragmentName: 'ReactionSummary',
      },
      existingSummary => {
        if (!existingSummary) {
          return existingSummary;
        }

        const existingReactions = existingSummary.emojiReactions || [];
        const existingIndex = existingReactions.findIndex(
          entry => entry.key === emoji,
        );

        if (existingIndex < 0) {
          // Emoji not found, nothing to update
          return existingSummary;
        }

        const existingEntry = existingReactions[existingIndex];
        const existingValue = existingEntry.value;
        const newCount = (existingValue?.reactionCount || 1) - 1;

        if (newCount <= 0) {
          // Remove the emoji reaction entirely
          return {
            ...existingSummary,
            emojiReactions: existingReactions.filter(
              (_, index) => index !== existingIndex,
            ),
          };
        }

        // Decrement count and remove current identity
        return {
          ...existingSummary,
          emojiReactions: existingReactions.map((entry, index) => {
            if (index !== existingIndex) {
              return entry;
            }

            return {
              ...entry,
              value: {
                ...existingValue,
                reactionCount: newCount,
                identities: (existingValue?.identities || []).filter(
                  identity =>
                    getTypeOrNull(identity)?.name !== currentIdentity.name,
                ),
              },
            };
          }),
        };
      },
    );
  };
}
